import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, RefreshCw, Link as LinkIcon, Unlink, ShieldAlert } from "lucide-react";

const money = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * BankSyncCard — admin card for Stripe Financial Connections. In dev the
 * link button will 501 if Stripe FC isn't enabled on the test account; we
 * surface that gracefully so the UI still demos the flow.
 */
export default function BankSyncCard() {
  const { currentProperty } = useProperty();
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fcAvailable, setFcAvailable] = useState(null); // null=unknown, true/false
  const [fcStatus, setFcStatus] = useState(null); // { enabled, reason, detail, mode }

  const load = async () => {
    try {
      const { data } = await api.get("/admin/bank/accounts", {
        params: { property_id: currentProperty?.id || "all" },
      });
      setAccounts(data.accounts || []);
    } catch (e) { if (typeof console !== 'undefined') console.debug('components/BankSyncCard.jsx: swallowed', e); }
  };
  const loadFcStatus = async () => {
    try {
      const { data } = await api.get("/admin/bank/fc-status");
      setFcStatus(data);
      if (data && data.enabled === false) setFcAvailable(false);
      if (data && data.enabled === true) setFcAvailable(true);
    } catch (e) { if (typeof console !== 'undefined') console.debug('components/BankSyncCard.jsx: swallowed', e); }
  };
  useEffect(() => { load(); loadFcStatus(); }, [currentProperty?.id]);

  const startLink = async () => {
    if (!currentProperty?.id) return toast.error("Pick a property first");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bank/session", { property_id: currentProperty.id });
      // If we're in dev with no Stripe.js loaded, fall back to a demo message
      if (typeof window !== "undefined" && window.Stripe) {
        const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");
        const result = await stripe.collectFinancialConnectionsAccounts({
          clientSecret: data.client_secret,
        });
        if (result.error) throw new Error(result.error.message);
        const { data: done } = await api.post("/admin/bank/complete", { session_id: data.session_id });
        toast.success(`Linked ${done.count} bank account${done.count === 1 ? "" : "s"}`);
        load();
      } else {
        toast.info("Stripe.js not loaded — session created, complete linking from your production frontend");
        setFcAvailable(true);
      }
    } catch (e) {
      if (e?.response?.status === 501) {
        setFcAvailable(false);
        toast.error("Stripe Financial Connections isn't enabled on this Stripe account. Enable it in Dashboard → Payment methods.");
      } else {
        toast.error(apiErrorMessage(e));
      }
    }
    setBusy(false);
  };

  const refresh = async (aid) => {
    setBusy(true);
    try {
      await api.post(`/admin/bank/accounts/${aid}/refresh`);
      toast.success("Refresh queued — poll in a few seconds");
      setTimeout(load, 3000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };
  const disconnect = async (aid) => {
    if (!confirm("Disconnect this bank account?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/bank/accounts/${aid}`);
      toast.success("Disconnected"); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  return (
    <Card className="border-white/10 bg-card p-5" data-testid="bank-sync-card">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="overline flex items-center gap-1"><Building2 className="h-3 w-3" /> Bank Sync</div>
          <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
            Stripe Financial Connections
            {fcStatus && fcStatus.enabled === true && (
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[10px] uppercase tracking-widest" data-testid="fc-status-pill-on">FC on</Badge>
            )}
            {fcStatus && fcStatus.enabled === false && (
              <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10 text-[10px] uppercase tracking-widest" data-testid="fc-status-pill-off">FC off</Badge>
            )}
          </div>
          <div className="text-sm text-zinc-500 mt-1">
            Link the property&apos;s operating account for balances + transaction feeds.
          </div>
        </div>
        <Button data-testid="bank-link-btn" onClick={startLink} disabled={busy} className="bg-orange-500 hover:bg-orange-600 shrink-0">
          <LinkIcon className="h-4 w-4 mr-2" /> {busy ? "Linking…" : "Link bank"}
        </Button>
      </div>
      {fcAvailable === false && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 flex items-start gap-2 mb-3" data-testid="bank-fc-unavailable">
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-amber-100 mb-1">Stripe Financial Connections isn&apos;t enabled on your Stripe account.</div>
            <div>Takes ~5 min in the Stripe dashboard. Enable <strong>Balances</strong> + <strong>Transactions</strong> capabilities, then click Link bank again — no code change needed.</div>
            <div className="mt-2 flex items-center gap-3">
              <a href="https://dashboard.stripe.com/settings/payment_methods" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-amber-100" data-testid="bank-fc-enable-link">
                Open Stripe → Payment methods →
              </a>
              <button onClick={() => { setFcAvailable(null); startLink(); }} className="underline underline-offset-2 hover:text-amber-100" data-testid="bank-fc-retry">
                I enabled it — retry
              </button>
            </div>
          </div>
        </div>
      )}
      {accounts.length === 0 ? (
        <div className="text-center py-6 text-sm text-zinc-500" data-testid="bank-empty-state">
          No bank accounts linked yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="bank-accounts-list">
          {accounts.map((a) => (
            <div key={a.stripe_account_id} className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3" data-testid={`bank-account-${a.stripe_account_id}`}>
              <Building2 className="h-4 w-4 text-orange-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">
                  {a.institution_name || "Bank"} — {a.display_name || a.subcategory}
                  <span className="text-zinc-500 ml-2">•••• {a.last4 || "????"}</span>
                </div>
                <div className="text-[11px] text-zinc-500">
                  {a.status || "unknown"} · Balance: {money(a.balance?.cash?.available ?? a.balance?.current)}
                  {a.last_synced_at && <span className="ml-2">Last synced {a.last_synced_at.slice(0, 10)}</span>}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => refresh(a.stripe_account_id)} disabled={busy} data-testid={`bank-refresh-${a.stripe_account_id}`}>
                <RefreshCw className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-300" onClick={() => disconnect(a.stripe_account_id)} disabled={busy} data-testid={`bank-disconnect-${a.stripe_account_id}`}>
                <Unlink className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
